import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import { css } from "@emotion/react";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Dialog from "components/DesignComponents/Dialog";
import AuthContext from "context/auth/authContext";
import { isAuthenticatorType } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles";
import { ENDPOINTS } from "shared/types/enums";
import { DialogHandler } from "shared/types/interfaces";

import { sendCodeToEmail } from "../functions";
import { SendOtpCodeToDisable } from "./content";
import { DISABLE_AUTHENTICATION_STEPS } from "./types";

const DisableTwoFactorAuthentication = ({ isOpen, close }: DialogHandler) => {
    const { authType, user, checkAuth, resetAuthType } =
        useContext(AuthContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const { fromMd } = useMediaQueries();

    const [step, setStep] = useState(DISABLE_AUTHENTICATION_STEPS.Confirmation);
    const [isLoading, setIsLoading] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [error, setError] = useState<string | null>(null);

    const changeStep = (newStep: DISABLE_AUTHENTICATION_STEPS) =>
        setStep(newStep);

    const changeOtpCode = (newValue: string) => {
        setError(null);
        setOtpCode(newValue);
    };

    const confirmationSubmit = () => {
        if (isAuthenticatorType(authType)) {
            changeStep(DISABLE_AUTHENTICATION_STEPS.SendOtpCode);
        } else {
            const successCallback = () =>
                changeStep(DISABLE_AUTHENTICATION_STEPS.SendEmailCode);

            sendCodeToEmail(
                setIsLoading,
                successCallback,
                postData,
                handleResponse
            );
        }
    };

    const sendOtpCodeSubmit = async () => {
        try {
            setIsLoading(true);

            const response = await postData(ENDPOINTS.TfaDisable, {
                otp_code: otpCode,
                two_factor_auth_type: authType
            });

            checkAuth();
            close();
            handleResponse(response);
        } catch (error) {
            const err = error as AxiosError;

            if (err.response?.status === 400) {
                setError(t("Security##2FA##send otp code##incorrect code"));
            } else {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    const steps = {
        [DISABLE_AUTHENTICATION_STEPS.Confirmation]: {
            hasActionSeparator: false,
            title: t("Security##2FA##disable##confirmation##title"),
            description: t("Security##2FA##disable##confirmation##description"),
            secondaryBtn: t("Button##close"),
            submitBtn: t("Button##yes"),
            isSubmitBtnDisabled: false,
            content: null,
            onSubmit: confirmationSubmit
        },
        [DISABLE_AUTHENTICATION_STEPS.SendOtpCode]: {
            hasActionSeparator: true,
            title: t("Security##2FA##disable##title"),
            description: t(
                "Security##2FA##disable##send otp code##description"
            ),
            secondaryBtn: t("Button##close"),
            submitBtn: t("Button##disable"),
            isSubmitBtnDisabled: otpCode.length < 6,
            content: (
                <SendOtpCodeToDisable
                    authType={authType!}
                    error={error}
                    isLoading={isLoading}
                    changeCode={changeOtpCode}
                />
            ),
            onSubmit: sendOtpCodeSubmit
        },
        [DISABLE_AUTHENTICATION_STEPS.SendEmailCode]: {
            hasActionSeparator: true,
            title: t("Security##2FA##disable##title"),
            description: t(
                "Security##delete account##confirmation##verification code description##email"
            ),
            secondaryBtn: t("Button##close"),
            submitBtn: t("Button##disable"),
            isSubmitBtnDisabled: otpCode.length < 6,
            content: (
                <SendOtpCodeToDisable
                    authType={authType!}
                    error={error}
                    isLoading={isLoading}
                    changeCode={changeOtpCode}
                />
            ),
            onSubmit: sendOtpCodeSubmit
        }
    };

    const hasActionSeparator = steps[step].hasActionSeparator;
    const title = steps[step].title;
    const description = steps[step].description;
    const secondaryBtnName = steps[step].secondaryBtn;
    const submitBtnName = steps[step].submitBtn;
    const isDisabled = steps[step].isSubmitBtnDisabled;
    const content = steps[step].content;
    const onSubmit = steps[step].onSubmit;

    return (
        <Dialog
            open={isOpen}
            close={close}
            isTitleSeparator
            isActionsSeparator={hasActionSeparator}
            extendToMaxWidth
            title={title}
            description={description}
            submit={onSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setStep(DISABLE_AUTHENTICATION_STEPS.Confirmation);
                        setOtpCode("");
                        setError(null);

                        if (user && !user.two_factor_auth_enabled) {
                            resetAuthType();
                        }
                    }
                }
            }}
            actions={
                <>
                    <Button
                        fullWidth
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        disabled={isLoading}
                        onClick={close}
                    >
                        {secondaryBtnName}
                    </Button>

                    {submitBtnName && (
                        <Button
                            fullWidth
                            color={BUTTON_COLORS.Primary}
                            size={BUTTON_SIZES.Normal}
                            variant={BUTTON_VARIANTS.TextOnly}
                            type="submit"
                            disabled={isDisabled}
                            isLoading={isLoading}
                        >
                            {submitBtnName}
                        </Button>
                    )}
                </>
            }
            css={css(fromMd && maxDialogWidth)}
        >
            {content}
        </Dialog>
    );
};

export default DisableTwoFactorAuthentication;
